// Instance
import MainAPI from "@/app/apis/main/main-api.js";

// Endpoints
import { API, ENDPOINTS } from "@/app/apis/main/_connection/connection-api.js";

const queryString = require("querystring")

class BreedingManagementAPI extends MainAPI {
    async getBreederDamAndSireList(queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/list?${queryString.stringify(queryParams)}`)
	}
	/**
	 * Retrieves a list of breeders based on the specified gender from the API.
	 * 
	 * @param {("Dam" | "Sire")} gender - The gender of the breeders to retrieve. It can be either "Dam" or "Sire".
	 * @returns {Promise<any>} A promise that resolves to the API response containing the list of breeders.
	 */
	async getBreederDamAndSireListByGender(gender) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${gender}/list`);
	}

	/**
	 * Submits mating details for a dam and sire to the API.
	 * 
	 * @param {Object} matingDetails - The details of the mating to be submitted.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after submitting the mating details.
	 */
	async addDamAndSireMatingDetails(matingDetails) {
		return API.post(`${ENDPOINTS.POST_MATING_DETAILS}`, matingDetails);
	}

	async getMatingList(queryParams) {
		return API.get(`${ENDPOINTS.POST_DAM_SIRE}/mating-list?${queryString.stringify(queryParams)}`)
	}

	/**
	 * Fetches the mating details of a Dam or Sire by ID by sending a GET request.
	 *
	 * @async
	 * @function getDamAndSireMatingDetails
	 * @param {string} identifier - Specifies whether to retrieve details for a "dam" or "sire".
	 * @param {number} detailsId - The ID of the Dam or Sire for which to fetch mating details.
	 * @returns {Promise<Object>} The response from the API containing the Dam/Sire mating details.
	 * @throws {Error} If the API request fails.
 */
	async getDamAndSireMatingDetails(identifier, detailsId) {
		if(identifier === "dam"){
			return API.get(`${ENDPOINTS.GET_DAM_SIRE_MATING}?damId=${detailsId}`)
		}
		
		return API.get(`${ENDPOINTS.GET_DAM_SIRE_MATING}?matingId=${detailsId}`)
	}

	/**
	 * Updates mating details for a specific mating ID using the API.
	 * 
	 * @param {number} matingId - The ID of the mating to be updated.
	 * @param {Object} matingDetails - The updated details of the mating.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after updating the mating details.
	 */
	async updateDamAndSireMatingDetails(matingId, matingDetails) {
		return API.put(`${ENDPOINTS.PUT_MATING_DETAILS}/${matingId}`, matingDetails);
	}

	async deleteMatingDetails(id) {
		return API.delete(`${ENDPOINTS.POST_DAM_SIRE}/${id}/mating-details`)
	}

	async getPuppyManagementList(queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/puppy-management-list?${queryString.stringify(queryParams)}`)
	}

	async getLitterList(id, queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/${id}/litter-list?${queryString.stringify(queryParams)}`)
	}

	// *********************************** Ovulation / Gestation ****************************************************
	
	/**
	 * Saves ovulation and gestation details for a dam.
	 *
	 * @async
	 * @function saveOvulationAndGestation
	 * @param {Object} details - The ovulation and gestation details to save.
	 * @param {number} details.damId - The ID of the dam.
	 * @param {string} details.matingDate - The mating date of the dam (in ISO 8601 format).
	 * @param {string} details.ovulationStartDate - The ovulation start date of the dam (in ISO 8601 format).
	 * @returns {Promise<Object>} A promise that resolves to the response from the API.
	 */
	async saveOvulationAndGestation(details) {
		return API.post(ENDPOINTS.POST_OVULATION_GESTATION, details);
	}

	/**
	 * Retrieves ovulation and gestation details for a specific dam.
	 *
	 * @async
	 * @function getDamOvulationAndGestationDetails
	 * @param {number} damId - The ID of the dam whose cycle dates are to be retrieved.
	 * @param {number} matingId - The ID of the mating whose cycle dates are to be retrieved.
	 * @returns {Promise<Object>} A promise that resolves to the response from the API containing the cycle dates.
	 */
	async getDamOvulationAndGestationDetails(damId, matingId) {

		if(matingId){
			return API.get(`${ENDPOINTS.GET_OVULATION_GESTATION}/${damId}/details?matingId=${matingId}`);
		}
		
		return API.get(`${ENDPOINTS.GET_OVULATION_GESTATION}/${damId}/details`);
	}

	// *********************************** Litters ****************************************************
	
	/**
	 * Sends a request to save the details of a litter.
	 * 
	 * @param {Object} details - The details of the litter to be saved.
	 * @param {number|null} [details.breedId] - The ID of the breed. If no breed, use `null`.
	 * @param {number} details.matingId - The ID of the mating.
	 * @param {string} details.name - The name of the litter.
	 * @param {string} [details.status] - The status of the litter. Example: `LITTER_STATUS.ACTIVE`. Optional.
	 * @param {string|null} [details.dateOfBirth] - The date of birth of the litter in ISO format. If not provided, use `null`.
	 * @param {string} details.identifyingMarks - Identifying marks of the litter.
	 * @param {string} details.collarColour - The colour of the collar.
	 * @param {number} details.birthWeight - The birth weight of the litter.
	 * @param {string} details.unitsOfWeight - The units of weight. Always "kg".
	 * @param {string} details.gender - The gender of the litter.
	 * @param {number} details.breederSysId - The system ID of the breeder.
	 * @param {string} [details.id] - Optional ID of the litter.
	 * @param {string} [details.status] - Optional status of the litter.
	 * 
	 * @returns {Promise<Object>} The response from the API.
	 */
	async saveLittersDetails(details, littersImageUploads) {
		let newFormData = new FormData()

		newFormData.append("formData", JSON.stringify(details));

		littersImageUploads.forEach((image, index) => {
			if (image) {
				newFormData.append(`dogImageUpload[${index}]`, image);
			}
		});
		
		return API.post(ENDPOINTS.POST_LITTERS, newFormData, {
			header: {
				"Content-Type": "multipart/form-data",
			},
		});
	}
		

	/**
	 * Deletes the litter details associated with the specified ID.
	 *
	 * This function sends a DELETE request to the API endpoint to remove
	 * the litter details for the given ID.
	 *
	 * @async
	 * @function deleteLitterDetails
	 * @param {number|string} id - The ID of the litter details to be deleted.
	 * @returns {Promise<Object>} A promise that resolves to the response from the API.
	 */
	async deleteLitterDetails(id) {
		return API.delete(`${ENDPOINTS.POST_DAM_SIRE}/${id}/delete-litter`)
	}

	/**
	 * Asynchronously retrieves the details of the dam and sire for a specific litter.
	 *
	 * This function makes an API call to fetch details using the provided litter ID.
	 *
	 * @async
	 * @function getDamAndSireLitterDetails
	 * @param {string} litterId - The unique identifier of the litter for which details are being requested.
	 * @returns {Promise<Object>} A promise that resolves to an object containing the details of the dam and sire.
	 * 
	 * @throws {Error} Throws an error if the API call fails or if the provided litterId is invalid.
	 *
	 */
	async getDamAndSireLitterDetails(litterId) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/litter/${litterId}/details`)
	}

	/**
	 * Asynchronously saves the details of puppies for a specific litter.
	 *
	 * This function makes a POST request to save the provided details associated with the given litter ID.
	 *
	 * @async
	 * @function saveLitterPuppyDetails
	 * @param {string} litterId - The unique identifier of the litter for which puppy details are being saved.
	 * @param {Object} details - An object containing the details of the puppies to be saved.
	 * @returns {Promise<Object>} A promise that resolves to the response object from the API, typically containing confirmation of the saved details.
	 *
	 * @throws {Error} Throws an error if the API call fails or if the provided litterId or details are invalid.
	 *
	 */
	async saveLitterPuppyDetails(litterId, details) {

		let newFormData = new FormData()
		newFormData.append("formData", JSON.stringify(details.formData))

		if (details.images.length > 0) {
			details.images.forEach((image) => {
				newFormData.append("images", image)
			})
		}

		return API.put(`${ENDPOINTS.GET_DAM_SIRE}/litter/${litterId}/details`, newFormData, {
			header: {
				"Content-Type": "multipart/form-data",
			},
		})
	}

	async getDamDiaryDetails(damId) {
		return API.get(`${ENDPOINTS.GET_OVULATION_GESTATION}/history/${damId}`)
	}


	// *********************************** Mating / Ovulation / Gestation / Whelping ****************************************************
	/**
	 * Saves mating and ovulation/gestation/whelping details for a dam and sire.
	 *
	 * @async
	 * @function saveMatingAndOvulationGestationWhelping
	 * @param {Object} details - The details to be saved.
	 * @param {Object} details.matingDetails - Mating information for the dam and sire.
	 * @param {number|null} details.matingDetails.damId - ID of the dam, or `null` if not selected.
	 * @param {number|null} details.matingDetails.sireId - ID of the sire, or `null` if not selected.
	 * @param {string|null} details.matingDetails.note - Additional note related to the mating details.
	 * @param {Object} details.cycleDetails - Cycle-related details for the dam.
	 * @param {number} details.cycleDetails.damId - ID of the dam.
	 * @param {string|null} details.cycleDetails.ovulationStartDate - The ovulation start date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @param {string|null} details.cycleDetails.matingDate - The mating date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @returns {Promise<Object>} The response from the API.
	 */
	async saveMatingAndOvulationGestationWhelping(details) {
		return API.post(ENDPOINTS.POST_MATING_AND_CYCLE_DATES, details);
	}
	
	/**
	 * Updates mating and ovulation/gestation/whelping details for a dam and sire.
	 *
	 * @async
	 * @function updateMatingAndOvulationGestationWhelping
	 * @param {Object} details - The details to be updated.
	 * @param {Object} details.matingDetails - Mating information for the dam and sire.
	 * @param {number|null} details.matingDetails.damId - ID of the dam, or `null` if not selected.
	 * @param {number|null} details.matingDetails.sireId - ID of the sire, or `null` if not selected.
	 * @param {string|null} details.matingDetails.note - Additional note related to the mating details.
	 * @param {Object} details.cycleDetails - Cycle-related details for the dam.
	 * @param {number} details.cycleDetails.damId - ID of the dam.
	 * @param {string|null} details.cycleDetails.ovulationStartDate - The ovulation start date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @param {string|null} details.cycleDetails.matingDate - The mating date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @returns {Promise<Object>} The response from the API.
	 */
	async updateMatingAndOvulationGestationWhelping(details) {
		return API.put(ENDPOINTS.PUT_MATING_AND_CYCLE_DATES, details);
	}

	/**
 	* Updates the mating status.
 	* 
	* @param {number} matingId - The ID of the mating.
 	* @param {Object} details - The details of the mating status update.
 	* @param {string} details.status - The status of the mating (successful or unsuccessful).
 	* @returns {Promise} - A promise that resolves when the mating status is updated.
 	*/
	 async updateMatingStatus(matingId, details) {
		return API.put(`${ENDPOINTS.PUT_MATING_STATUS}/${matingId}`, details);
	}


	/**
	 * Fetches the litter reminder notes for a given mating ID.
	 * 
	 * @param {number} matingId - The ID of the mating.
	 * @param {Object} queryParams - The query parameters for the request.
	 * @param {number} queryParams.page - The page number for pagination.
	 * @param {number} queryParams.limit - The number of items per page.
	 * @param {string} queryParams.sortBy - The field to sort by.
	 * @param {boolean} queryParams.desc - Whether to sort in descending order.
	 * @returns {Promise} - A promise that resolves to the API response.
	 */
	async getLitterReminderNotes(matingId, queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/reminder-notes/${matingId}?${queryString.stringify(queryParams)}`)
	}

	async getLitterReminderNotesById(matingId, reminderId) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/reminder-notes-by-id/${matingId}/${reminderId}`)
	}

	async saveLitterReminderNotes(reminderDetails) {
		return API.post(`${ENDPOINTS.GET_DAM_SIRE}/reminder-notes`, reminderDetails);
	}

	async updateLitterReminderNotes(reminderDetails) {
		return API.put(`${ENDPOINTS.GET_DAM_SIRE}/update-reminder-notes`, reminderDetails);
	}

	async getLitterNotes(matingId, queryParams) {
		return API.get(`${ENDPOINTS.GET_DAM_SIRE}/litter-notes/${matingId}?${queryString.stringify(queryParams)}`)
	}
	
	async saveLitterNotes(noteDetails) {
		return API.post(`${ENDPOINTS.GET_DAM_SIRE}/litter-notes`, noteDetails);
	}

	async updateLitterNotes(noteDetails) {
		return API.put(`${ENDPOINTS.GET_DAM_SIRE}/update-litter-notes`, noteDetails);
	}

	async updateDamWhelpDateAndTypeOfBirth(details) {
		return API.put(`${ENDPOINTS.PUT_DAM_WHELP_DATE_AND_TYPE_OF_BIRTH}`, details);
	}

	// *********************************** Waitlist ****************************************************
	/**
	 * Allocates a puppy by sending form data and an optional contract document
	 * to the server using a multipart/form-data request.
	 *
	 * @async
	 * @function allocatePuppy
	 * @param {Object} details - The details to be submitted.
	 * @param {Object} details.formData - The structured data related to the puppy allocation (e.g. user info, pricing).
	 * @param {File} [details.contractDocument] - Optional contract document to be uploaded (e.g. PDF or image).
	 * @returns {Promise} The API response from the server.
	 */
	async allocatePuppy(details) {
        const formData = new FormData();

		formData.append("formData", JSON.stringify(details.formData));

		if(details.contractDocument && details.contractDocument.size){
			formData.append("contract", details.contractDocument)
		}

		return API.post(`${ENDPOINTS.POST_ALLOCATE_PUPPY}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
	}


	/**
	 * Sends a GET request to retrieve the list of puppies from a litter that are available for allocation
	 * based on the provided mating ID.
	 *
	 * @param {string|number} matingId - The ID of the mating record to fetch available puppies for.
	 * @returns {Promise} A promise that resolves to the Axios response containing the list of puppies.
	 */
	async getPuppiesLitterToAllocate(matingId) {
		return API.get(`${ENDPOINTS.GET_PUPPIES_TO_ALLOCATE}/${matingId}`)
	}

	async getWaitlist(matingId, queryParams) {
		return API.get(`${ENDPOINTS.GET_WAITLIST}/${matingId}?${queryString.stringify(queryParams)}`)
	}
	async deleteAllocatedPuppyById(allocatedPuppyId) {
		return API.delete(`${ENDPOINTS.GET_ALLOCATED_PUPPY_DETAILS}/${allocatedPuppyId}`)
	}

	async getAllocatedPuppyById(allocatedPuppyId) {
		return API.get(`${ENDPOINTS.GET_ALLOCATED_PUPPY_DETAILS}/${allocatedPuppyId}`)
	}

	async updateAllocatedPuppy(allocatedPuppyId, details) {
		const formData = new FormData();

		formData.append("formData", JSON.stringify(details.formData));

		if(details.contractDocument && details.contractDocument.size){
			formData.append("contract", details.contractDocument)
		}

		return API.put(`${ENDPOINTS.PUT_ALLOCATED_PUPPY_DETAILS}/${allocatedPuppyId}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
	}
}

export default new BreedingManagementAPI();
